<script>
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    insertHistoryAction
} from '@app/store/historyStore.svelte.js';
import {
    authState
} from '@app/store/authStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    onDestroy
} from 'svelte';
import { SvelteSet } from 'svelte/reactivity';
import {
    fade
} from 'svelte/transition';
import Papa from 'papaparse';
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import {
    BarLoader,
    Circle
} from 'svelte-loading-spinners'
import * as m from '@app/paraglide/messages.js';

/* CC LIB */
import _EventGroup from '@cclib/classes/eventGroup.js';
import autoFormat from '@cclib/functions/eventGroups/autoFormat.js';
import fixOverlap from '@cclib/functions/eventGroups/fixOverlap.js';
/* Firebase */
import { auth, functions } from '@app/configs/firebase.js';
import db from '@app/configs/firestore.js';
import storage from '@app/configs/storage.js';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, query, where, orderBy, getDocs, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';

import {
    saveAs
} from 'file-saver';

let jobs = $state([]),
    selectedJobIds = $state(new SvelteSet()),
    lastClickedJobId = $state(null),
    userId = auth.currentUser.uid,
    email = auth.currentUser.email,
    teamId = authState.team ? authState.team.id : null,
    dbRef = authState.team ? collection(db, "teams", teamId, "jobs") : collection(db, "users", userId, "jobs"),
    storageRef = ref(storage),
    storagePath = authState.team ? `teams/${teamId}/translations/` : `users/${userId}/translations/`,
    deletingFile = $state(false),
    loading = $state(false),
    autoRefresh = $state(true);

let selectedJobs = $derived(jobs.filter(j => selectedJobIds.has(j.id)));
let singleSelectedJob = $derived(selectedJobs.length === 1 ? selectedJobs[0] : null);
let importableSelectedJobs = $derived(selectedJobs.filter(j => !j.deleted && j.status === 'Passed'));

let eventGroupDefaults = JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {}
let maxChars = eventGroupDefaults.maxChars || 32;
let maxLines = eventGroupDefaults.maxLines || 3;
let minDuration = 0.5;
let allowOrphanWords = false;

let dateRange = $state("week"),
    startDate = $state(),
    endDate = $state(),
    statusFilter = $state("All"),
    pending = false,
    gettingJobs = $state(getTranslationJobs());

let autoRefreshInterval = setInterval(function () {
    if (!loading && !pending){
        gettingJobs = getTranslationJobs();
    }
}, 10000);

onDestroy(() => {
    if (autoRefreshInterval){
        clearInterval(autoRefreshInterval);
    }    
});

function disableAutoRefresh(){
    clearInterval(autoRefreshInterval);
    autoRefreshInterval = undefined;
    autoRefresh = false;
}

function enableAutoRefresh(){
    if (autoRefreshInterval){
        clearInterval(autoRefreshInterval);
    }

    autoRefreshInterval = setInterval(function () {
        if (!loading && !pending){            
            gettingJobs = getTranslationJobs();
        }
    }, 10000);

    autoRefresh = true;
}

function getDateRange() {
    let start, end;
    switch (dateRange) {
        case "custom":
            start = new Date(startDate);
            end = new Date(endDate);
            break;
        case "week":
            // code block
            start = new Date();
            end = new Date();
            start.setHours(start.getHours() - 168);
            break;
        case "month":
            // code block
            start = new Date();
            end = new Date();
            start.setMonth(start.getMonth() - 1);
            break;
        case "year":
            // code block
            start = new Date();
            end = new Date();
            start.setFullYear(start.getFullYear() - 1);
            break;
        default:
            start = new Date();
            end = new Date();
            start.setHours(start.getHours() - 24);
    }

    return {
        start: start,
        end: end
    }

}

function getTranslationJobs() {
    if (pending === true){
        return;
    }
    pending = true;
    let queryDateRange = getDateRange();
    //console.log(queryDateRange);

    selectedJobIds.clear();
    lastClickedJobId = null;
    jobs = [];

    return getDocs(query(dbRef,
        where('createdOn', ">=", queryDateRange.start),
        where('createdOn', "<=", queryDateRange.end),
        where('type', "==", "translation"),
        where('status', "in", statusFilter === 'All' ? ["Submitted", "Passed", "Failed", "Submitted", "In Progress"] : [statusFilter]),
        orderBy('createdOn', 'desc')))
        .then((querySnapshot) => {
            querySnapshot.forEach((doc) => {
                if (!doc.data().archived){
                    jobs = [...jobs, doc.data()];
                }
            });

            if(allJobsComplete(jobs)){
                disableAutoRefresh();
            }
        }).catch((error) => {
            console.log("Error getting documents: ", error);
            toast.push(m.error_getting_translations({ error: error.message }), {
                classes: ["toast-danger"]
            });
        }).finally(() => {
            pending = false;
        });
}

function allJobsComplete(jobs){
    let inProgressJob = jobs.findIndex(job =>{
        return job.progress !== 100;
    });

    if (inProgressJob > -1){
        return false;
    } else {
        return true;
    }
}

function handleRowClick(event, job){
    if (event.shiftKey && lastClickedJobId){
        let anchorIndex = jobs.findIndex(j => j.id === lastClickedJobId);
        let currentIndex = jobs.findIndex(j => j.id === job.id);

        if (anchorIndex === -1 || currentIndex === -1){
            selectedJobIds.clear();
            selectedJobIds.add(job.id);
            lastClickedJobId = job.id;
            return;
        }

        let start = Math.min(anchorIndex, currentIndex);
        let end = Math.max(anchorIndex, currentIndex);
        for (let i = start; i <= end; i++){
            selectedJobIds.add(jobs[i].id);
        }
        return;
    }

    if (event.ctrlKey || event.metaKey){
        if (selectedJobIds.has(job.id)){
            selectedJobIds.delete(job.id);
        } else {
            selectedJobIds.add(job.id);
        }
        lastClickedJobId = job.id;
        return;
    }

    selectedJobIds.clear();
    selectedJobIds.add(job.id);
    lastClickedJobId = job.id;
}

function archiveJob(){
    if (!singleSelectedJob){
        return;
    }
    deletingFile = true;
    updateDoc(doc(dbRef, singleSelectedJob.id), {
        archived: true
    }).then(() => {
        toast.push(m.job_archived_success(), {
            classes: ["toast-success"]
        });
        gettingJobs = getTranslationJobs();
    }).catch((e) => {
        toast.push(m.error_archiving_job({ error: e.message }), {
            classes: ["toast-danger"]
        });
        console.log(e, e.message);
    }).finally(() => {
        deletingFile = false;
    });
}

async function importTranslation() {
    let jobsToImport = importableSelectedJobs;
    if (jobsToImport.length === 0){
        return;
    }

    disableAutoRefresh();
    loading = true;

    if (jobsToImport.length === 1){
        toast.push(m.loading_translation_please_wait(), {
            classes: ["toast-info"]
        });
    } else {
        toast.push(m.loading_translations_please_wait({ count: jobsToImport.length }), {
            classes: ["toast-info"]
        });
    }

    const results = await Promise.allSettled(
        jobsToImport.map(async (job) => {
            const url = await getDownloadURL(ref(storageRef, storagePath + job.id + ".json"));
            const res = await fetch(url);
            const data = await res.json();
            return { job, data };
        })
    );

    let importedCount = 0;
    let failedJobs = [];

    results.forEach((result, jobIdx) => {
        if (result.status !== "fulfilled"){
            failedJobs.push(jobsToImport[jobIdx]);
            console.log("Failed to load translation", jobsToImport[jobIdx].id, result.reason);
            return;
        }

        const resJson = result.value.data;

        resJson.forEach((evg, index) => {
            /* Check if the Event Group exists in the project already */
            let indexOfEvg = eventGroupState.findIndex(eventGroup => {
                return eventGroup.id === evg.id;
            });

            let newEvg = {...(new _EventGroup(evg))};
            newEvg.id = evg.id;

            if (indexOfEvg === -1){
                if (index > 0){
                    for (let i = 0; i < 2; i++){
                        newEvg = autoFormat(newEvg, Math.max(3, maxLines), maxChars, minDuration, allowOrphanWords, false);
                    }

                    newEvg = fixOverlap(newEvg);
                }

                eventGroupState.push(newEvg);
            }
        });

        importedCount++;
    });

    if (importedCount === 0){
        loading = false;
        toast.push(m.failed_to_load_translations_count({ count: failedJobs.length }), {
            classes: ["toast-danger"]
        });
        return;
    }

    projectState.selected = eventGroupState.length - 1;

    insertHistoryAction({
        name: importedCount === 1 ? "import translation" : "import translations",
        eventGroup: projectState.selected,
        snapshots: [{
            store: "eventGroupState",
            value: JSON.stringify(eventGroupState)
        }]
    });

    if (importedCount === 1){
        toast.push(m.translation_imported_success(), {
            classes: ["toast-success"]
        });
    } else {
        toast.push(m.translations_imported_success({ count: importedCount }), {
            classes: ["toast-success"]
        });
    }

    if (failedJobs.length > 0){
        toast.push(m.failed_to_load_translations_count({ count: failedJobs.length }), {
            classes: ["toast-danger"]
        });
    }

    hideModal();
}

async function deleteTranslation() {
    if (!singleSelectedJob){
        return;
    }

    deletingFile = true;
    //User SweetAlert to confirm deletion
    let swalRes = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.destructive,
        title: m.are_you_sure(),
        text: m.delete_translation_confirm_text(),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: m.yes()
    });

    if (!swalRes.isConfirmed){
        deletingFile = false;
        return;
    }

    httpsCallable(functions, 'v8DeleteTranslation')(singleSelectedJob).then(() => {
        deletingFile = false;
        toast.push(m.translation_file_deleted_success(), {
            classes: ["toast-success"]
        });
        
        gettingJobs = getTranslationJobs();
    }).catch(e => {
        toast.push(m.error_deleting_translation_file({ error: e.message }), {
            classes: ["toast-danger"]
        });

        deletingFile = false;
        console.log(e, e.message);
    });
}

function exportCsv() {
    //console.log(JSON.stringify(jobs))
    toast.push(m.exporting_results(), {
        classes: ["toast-info"]
    });

    let csv = Papa.unparse(
        JSON.stringify(
            jobs.map(job =>{
                return {
                    id: job.id,
                    projectId: job.projectId,
                    projectName: job.projectName,
                    progress: job.progress,
                    createdOn: job.createdOn.toDate(),
                    completedOn: job.completedOn ? job.completedOn.toDate() : '',
                    updatedOn: job.updatedOn.toDate(),
                    status: job.status,
                    statusMsg: job.statusMsg,
                    type: job.type,
                    characters: job.chars,
                    cost: job.cost,
                    provider: job.config.provider || "",
                    srcLanguage : job.config.srcLanguage,
                    trgLanguage : job.config.trgLanguage
                }
            })
        )
    );

    let fileBlob = new Blob([csv], {
        type: "text/csv;charset=utf-8"
    });

    saveAs(fileBlob, "report_translation_jobs.csv", {
        autoBom: true
    });
}
</script>

<div transition:fade={{duration: 100}} class="modal {modalState.selected === 'aiTranslationImport' ? 'show d-block' : ''}" role="dialog" tabindex="-1" id="aiTranslationImportModal" aria-labelledby="aiTranslationImportTitle" aria-describedby="aiTranslationImportDesc">
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 id="aiTranslationImportTitle" class="modal-title">{m.translation_import()}</h4>
                <button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal} id="closeAiTranslationImport"></button>
            </div>
            <div class="modal-body" id="aiTranslationImportDesc">
                <div class="btn-group float-end mb-2" role="group">
                    <button type="button" class="btn btn-outline-dark" onclick={exportCsv} disabled={jobs.length === 0}>{m.export_as_csv()}</button>
                </div>
                <!-- Search Form -->
                <form>
                    <div class="row g-1">
                        <div class="col-2">
                            <select class="form-select me-2 mb-2" bind:value={dateRange}>
                                <option value="today">{m.past_24_hours()}</option>
                                <option value="week">{m.past_week()}</option>
                                <option value="month">{m.past_month()}</option>
                                <option value="year">{m.past_year()}</option>
                                <option value="custom">{m.custom()}</option>
                            </select>
                        </div>
                        {#if dateRange === 'custom'}
                        <div class="col-2">
                            <input class="form-control me-2 mb-2" type="date" bind:value={startDate}/>
                        </div>
                        <div class="col-2">
                            <input class="form-control me-2 mb-2" type="date" bind:value={endDate}/>
                        </div>
                        {/if}
                        <div class="col-2">
                            <select class="form-select me-2 mb-2" bind:value={statusFilter}>
                                <option value="All">{m.all()}</option>
                                <option value="Submitted">{m.submitted()}</option>
                                <option value="In Progress">{m.in_progress()}</option>
                                <option value="Passed">{m.passed()}</option>
                                <option value="Failed">{m.failed()}</option>
                            </select>
                        </div>
                        <div class="col">
                            <div class="btn-group" role="group">
                                <button type="button" class="btn btn-light" disabled={dateRange === 'custom' && (!startDate || !endDate)}  onclick={() => {gettingJobs = getTranslationJobs()}}>{m.refresh()}</button>
                                <button type="button" class="btn {autoRefresh ? 'btn-primary' : 'btn-light'}" onclick={() => {autoRefresh ? disableAutoRefresh() : enableAutoRefresh() }}><i class="bi {autoRefresh ? 'bi-check-circle-fill' : 'bi bi-circle'}"></i> {m.auto_refresh()}</button>                                
                            </div>
                                                        
                        </div>                       
                        {#if dateRange === 'custom' && (!startDate || !endDate)}
                        <p class="text-danger small pt-2 me-2">{m.custom_date_range_required()}</p>
                        {/if}
                        {#if loading}
                        <div class="col">
                            <Circle size="30" color="#1eb4b2" unit="px" duration="1s"></Circle>
                        </div>
                        {/if}
                    </div>
                </form>
                <!-- Results Table -->
                <div id="TranslationTableDashboard" class="table-responsive rounded shadow border border-secondary">
                    <table class="table table-hover table-sm">
                        <thead class="bg-dark text-dark">
                            <tr>
                                <th class="text-truncate">{m.project_name()}</th>
                                <th class="text-truncate">{m.progress()}</th>
                                <th>{m.status()}</th>
                                <th class="text-truncate">{m.status_message()}</th>

                                <th class="text-truncate">{m.characters()}</th>
                                <th>{m.provider()}</th>
                                <th class="text-truncate">{m.source_language()}</th>
                                <th class="text-truncate">{m.target_language()}</th>
                                <th class="text-truncate">{m.file_present()}</th>
                                <th class="w-25">{m.job_id()}</th>
                                <th class="text-truncate">{m.submitted_by()}</th>
                                <th>{m.created_on()}</th>
                                <th>{m.completed_on()}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#await gettingJobs}
                            <tr>
                                <td colspan="13">
                                    <div style="padding: 10%; position:relative; left:105%;">
                                        <BarLoader size="160" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                                    </div>
                                </td>
                            </tr>
                            {:then}
                            {#each jobs as job (job.id)}
                            <tr onclick={(e) => handleRowClick(e, job)} class={selectedJobIds.has(job.id) ? 'table-warning' : ''}>
                                <!-- Project Name -->
                                <td class="text-truncate" title={job.projectName}>{job.projectName}</td>

                                <!-- Progress -->
                                <td class="align-middle">
                                    <div class="progress" style="width:150px">
                                        <div class="progress-bar {job.progress < 100 ? 'progress-bar-animated' : ''} {job.status === 'Failed' ? 'bg-danger' : ''} {job.status === 'Passed' ? 'bg-primary' : ''} progress-bar-striped" role="progressbar" aria-valuemin="0" aria-valuemax="100" style="width: {
                                            job.duration && job.status === 'In Progress' ? Math.min(99,(((Math.round(Date.now() / 1000)-job.createdOn.seconds)/(job.duration/2)) * 100).toFixed(0)) : job.progress
                                            }%">{
                                            job.duration && job.status === 'In Progress' ? Math.min(99,(((Math.round(Date.now() / 1000)-job.createdOn.seconds)/(job.duration/2)) * 100).toFixed(0)) : job.progress
                                            }%
                                        </div>
                                    </div>
                                </td>

                                <!-- Status -->
                                <td title={job.status} class="text-truncate {job.status === 'Passed' ? 'text-primary' : ''} {job.status === 'Failed' ? 'text-danger' : ''}">{job.status}</td>
                                <!-- Status Msg -->                                
                                <td title={job.statusMsg} class='text-truncate'>{job.statusMsg}</td>

                                <!-- Characters -->
                                <td title={job.chars.toLocaleString('en-US')} class='text-truncate'>{job.chars.toLocaleString('en-US')}</td>
                                <!-- Provider -->
                                <td class="text-truncate" title={job.config.provider}>{job.config.provider}</td>
                                <!-- Source Language -->
                                <td  title={job.config.srcLanguage}>{job.config.srcLanguage}</td>
                                <!-- Target Language -->
                                <td  title={job.config.trgLanguage}>{job.config.trgLanguage}</td>
                                <!-- File Present -->
                                <td title={job.statusMsg}><i class="bi {!job.deleted && job.status === 'Passed' && job.config.linkedGroup ? 'bi-check-lg text-primary' : 'bi-x-lg text-light'}"></i></td>
                                <!-- Job Id -->
                                <td class="text-truncate small" title={job.id}>{job.id}</td>
                                <!-- Submitted By -->
                                <td class="text-truncate" title={job.submittedBy || email}>{job.submittedBy || email}</td>
                                <!-- Created On -->
                                <td class="text-truncate" title={job.createdOn.toDate()}>{job.createdOn.toDate().toString().substring(0,24)}</td>
                                <!-- Completed On -->
                                <td class="text-truncate" title={job.completedOn ? job.completedOn.toDate() : ""}>{job.completedOn ? job.completedOn.toDate().toString().substring(0,24) : ""}</td>
                            </tr>
                            {:else}
                            <tr>
                                <td colspan="13" class="text-light text-center m-5 text-truncate">{m.no_results_to_display()}</td>
                            </tr>
                            {/each}
                            {/await}

                        </tbody>
                    </table>
                </div>
                <div class="d-flex justify-content-between align-items-center mt-2">
                    <small class="text-light">
                        {#if selectedJobIds.size > 0}
                            {m.selected_count({ count: selectedJobIds.size })}
                        {:else}
                            {m.multi_select_hint()}
                        {/if}
                    </small>
                    {#if selectedJobIds.size > 0}
                    <div class="d-flex gap-2">
                        <div class="btn-group" role="group">
                            {#if singleSelectedJob && singleSelectedJob.status !== "Passed"}
                            <button title={m.archive_job()} type="button" class="btn btn-info text-white" disabled={deletingFile} onclick={archiveJob}><i class="bi bi-archive"></i> {m.archive_job()}</button>
                            {/if}
                            {#if singleSelectedJob && !singleSelectedJob.deleted && singleSelectedJob.status === 'Passed' && singleSelectedJob.config.linkedGroup}
                            <button type="button" class="btn btn-danger text-white" disabled={deletingFile} onclick={deleteTranslation}><i class="bi bi-trash"></i> {m.delete()}</button>
                            {/if}
                        </div>
                        <div class="btn-group" role="group" aria-label={m.ai_translation_import_button_group_label()}>
                            <button type="button" class="btn btn-primary" onclick={importTranslation} disabled={loading || deletingFile || importableSelectedJobs.length === 0}>
                                <i class="bi bi-translate"></i>
                                {#if importableSelectedJobs.length > 1}
                                    {m.import_translations_count({ count: importableSelectedJobs.length })}
                                {:else}
                                    {m.import_translation()}
                                {/if}
                            </button>
                        </div>
                    </div>
                    {/if}
                </div>
            </div>
        </div>
    </div>
</div>

<style lang="css">
#TranslationTableDashboard {
    max-height: 500px;
    margin-bottom: 2%;
}
#TranslationTableDashboard tbody {
    user-select: none;
}
</style>
